import { useEffect, useState } from 'react';
import { Card, Button, Badge, Spinner, FormField } from '../components/ui';

const SCHEMES = [
  { value: 'dps',        label: 'Deposit Protection Service (DPS)' },
  { value: 'mydeposits', label: 'MyDeposits' },
  { value: 'tds',        label: 'Tenancy Deposit Scheme (TDS)' },
];

const SCHEME_LABELS = { dps:'DPS', mydeposits:'MyDeposits', tds:'TDS' };

const STATUS_MAP = {
  ok:       { status:'valid',    label:'Done' },
  overdue:  { status:'expired',  label:'Overdue' },
  missing:  { status:'due_soon', label:'Not done' },
};

function depositStatusBadge(s) {
  const m = STATUS_MAP[s] || STATUS_MAP.missing;
  return <Badge status={m.status} label={m.label} />;
}

function formatGBP(pence) {
  return `£${(pence / 100).toFixed(2)}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  return diff;
}

const EMPTY_FORM = {
  tenant_id: '', amount_gbp: '', scheme: 'dps', scheme_ref: '',
  received_date: '', protected_date: '', prescribed_info_date: '',
  status: 'unprotected', notes: '',
};

export default function Deposits() {
  const [deposits, setDeposits]   = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editID, setEditID]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [filterProp, setFilterProp] = useState('');

  const token = () => localStorage.getItem('token');
  const headers = () => { const ac = localStorage.getItem('lc_access_code'); return { 'Content-Type':'application/json', Authorization:`Bearer ${token()}`, ...(ac ? {'X-Access-Code': ac} : {}) }; };

  useEffect(() => {
    Promise.all([
      fetch('/api/deposits',    { headers: headers() }).then(r => r.json()),
      fetch('/api/properties',  { headers: headers() }).then(r => r.json()),
    ]).then(([d, p]) => {
      setDeposits(Array.isArray(d) ? d : []);
      setProperties(Array.isArray(p) ? p : []);
      setLoading(false);
    });
  }, []);

  // Tenants for selected property
  const [tenants, setTenants] = useState([]);
  const [selectedProp, setSelectedProp] = useState('');

  useEffect(() => {
    if (!selectedProp) { setTenants([]); return; }
    fetch(`/api/properties/${selectedProp}/tenants`, { headers: headers() })
      .then(r => r.json()).then(t => setTenants(Array.isArray(t) ? t : []));
  }, [selectedProp]);

  function openNew() {
    setForm(EMPTY_FORM);
    setSelectedProp('');
    setEditID(null);
    setShowForm(true);
  }

  function openEdit(d) {
    setForm({
      tenant_id:            d.tenant_id || '',
      amount_gbp:           d.amount_gbp ? String(d.amount_gbp / 100) : '',
      scheme:               d.scheme || 'dps',
      scheme_ref:           d.scheme_ref || '',
      received_date:        d.received_date || '',
      protected_date:       d.protected_date || '',
      prescribed_info_date: d.prescribed_info_date || '',
      status:               d.status || 'unprotected',
      notes:                d.notes || '',
    });
    setSelectedProp(d.property_id);
    setEditID(d.id);
    setShowForm(true);
  }

  async function handleSave() {
    if (!selectedProp) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        tenant_id:   form.tenant_id || null,
        amount_gbp:  Math.round(parseFloat(form.amount_gbp || 0) * 100),
        scheme_ref:  form.scheme_ref || null,
        protected_date:       form.protected_date || null,
        prescribed_info_date: form.prescribed_info_date || null,
        notes:       form.notes || null,
      };

      if (editID) {
        await fetch(`/api/deposits/${editID}`, { method:'PUT', headers: headers(), body: JSON.stringify(body) });
      } else {
        await fetch(`/api/properties/${selectedProp}/deposits`, { method:'POST', headers: headers(), body: JSON.stringify(body) });
      }

      // Refresh
      const d = await fetch('/api/deposits', { headers: headers() }).then(r => r.json());
      setDeposits(Array.isArray(d) ? d : []);
      setShowForm(false);
    } finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this deposit record?')) return;
    await fetch(`/api/deposits/${id}`, { method:'DELETE', headers: headers() });
    setDeposits(d => d.filter(x => x.id !== id));
  }

  const filtered = filterProp ? deposits.filter(d => d.property_id === filterProp) : deposits;

  // Summary counts
  const issues = deposits.filter(d => d.protection_status !== 'ok' || d.prescribed_status !== 'ok');

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  if (loading) return <Spinner />;

  return (
    <div className="fade-up">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:32, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ fontSize:32, fontWeight:300 }}>Deposit <em>Compliance</em></h1>
          <p style={{ color:'var(--ink-3)', marginTop:4 }}>Track deposit protection and prescribed information for every tenancy</p>
        </div>
        <Button onClick={openNew}>+ Add deposit</Button>
      </div>

      {/* Summary */}
      {issues.length > 0 && (
        <Card style={{ marginBottom:24, background:'var(--danger-bg, #fef2f2)', borderColor:'var(--danger)' }}>
          <p style={{ color:'var(--danger)', fontWeight:600, fontSize:15 }}>
            ⚠️ {issues.length} deposit{issues.length !== 1 ? 's' : ''} with compliance issues
          </p>
          <p style={{ fontSize:13, color:'var(--ink-3)', marginTop:4 }}>
            Failing to protect a deposit within 30 days or serve prescribed information can result in tenants claiming up to 3× the deposit amount.
          </p>
        </Card>
      )}

      {/* Filter */}
      {properties.length > 1 && (
        <div style={{ marginBottom:20 }}>
          <select value={filterProp} onChange={e => setFilterProp(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--border)', background:'var(--bg)', fontSize:14, color:'var(--ink)' }}>
            <option value="">All properties</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.address_line1}</option>)}
          </select>
        </div>
      )}

      {/* Deposits list */}
      {filtered.length === 0 ? (
        <Card style={{ textAlign:'center', padding:48 }}>
          <p style={{ fontSize:32, marginBottom:12 }}>💰</p>
          <p style={{ fontWeight:600, marginBottom:8 }}>No deposits recorded</p>
          <p style={{ color:'var(--ink-3)', fontSize:14, marginBottom:20 }}>Track deposit protection and prescribed information for every tenancy.</p>
          <Button onClick={openNew}>+ Add your first deposit</Button>
        </Card>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {filtered.map(d => {
            const days = daysUntil(d.protection_deadline);
            return (
              <Card key={d.id}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6, flexWrap:'wrap' }}>
                      <p style={{ fontWeight:600, fontSize:16 }}>{d.property_address}</p>
                      {d.tenant_name && <span style={{ fontSize:13, color:'var(--ink-3)' }}>· {d.tenant_name}</span>}
                      <Badge status={d.status === 'returned' ? 'valid' : d.status === 'protected' ? 'valid' : 'due_soon'}
                        label={d.status === 'returned' ? 'Returned' : d.status === 'protected' ? 'Protected' : 'Unprotected'} />
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:12, marginBottom:12 }}>
                      <div>
                        <p style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.05em' }}>Amount</p>
                        <p style={{ fontSize:18, fontWeight:600 }}>{formatGBP(d.amount_gbp)}</p>
                      </div>
                      <div>
                        <p style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.05em' }}>Scheme</p>
                        <p style={{ fontSize:14 }}>{SCHEME_LABELS[d.scheme] || d.scheme}</p>
                        {d.scheme_ref && <p style={{ fontSize:12, color:'var(--ink-3)' }}>Ref: {d.scheme_ref}</p>}
                      </div>
                      <div>
                        <p style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.05em' }}>Received</p>
                        <p style={{ fontSize:14 }}>{formatDate(d.received_date)}</p>
                      </div>
                      <div>
                        <p style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.05em' }}>30-day deadline</p>
                        <p style={{ fontSize:14, color: days !== null && days < 0 ? 'var(--danger)' : days !== null && days < 7 ? 'var(--warn)' : 'var(--ink)' }}>
                          {formatDate(d.protection_deadline)}
                          {days !== null && days >= 0 && <span style={{ fontSize:12, marginLeft:4, color:'var(--ink-3)' }}>({days}d left)</span>}
                        </p>
                      </div>
                    </div>

                    {/* Compliance rows */}
                    <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:13, color:'var(--ink-3)' }}>Protected:</span>
                        {depositStatusBadge(d.protection_status)}
                        {d.protected_date && <span style={{ fontSize:12, color:'var(--ink-3)' }}>{formatDate(d.protected_date)}</span>}
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:13, color:'var(--ink-3)' }}>Prescribed info:</span>
                        {depositStatusBadge(d.prescribed_status)}
                        {d.prescribed_info_date && <span style={{ fontSize:12, color:'var(--ink-3)' }}>{formatDate(d.prescribed_info_date)}</span>}
                      </div>
                    </div>

                    {d.notes && <p style={{ fontSize:13, color:'var(--ink-3)', marginTop:8, fontStyle:'italic' }}>{d.notes}</p>}
                  </div>

                  <div style={{ display:'flex', gap:8 }}>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id)} style={{ color:'var(--danger)' }}>Delete</Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, backdropFilter:'blur(2px)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 20px', overflowY:'auto' }}
          onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
            <h3 style={{ fontSize:20, marginBottom:20 }}>{editID ? 'Edit deposit' : 'Add deposit'}</h3>

            <FormField label="Property *">
              <select value={selectedProp} onChange={e => { setSelectedProp(e.target.value); setForm(f => ({ ...f, tenant_id:'' })); }}
                disabled={!!editID}>
                <option value="">Select property…</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.address_line1}</option>)}
              </select>
            </FormField>

            {selectedProp && tenants.length > 0 && (
              <FormField label="Tenant">
                <select value={form.tenant_id} onChange={set('tenant_id')}>
                  <option value="">Select tenant…</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select>
              </FormField>
            )}

            <FormField label="Deposit amount (£) *">
              <input type="number" step="0.01" min="0" value={form.amount_gbp} onChange={set('amount_gbp')} placeholder="1200.00" />
            </FormField>

            <FormField label="Protection scheme *">
              <select value={form.scheme} onChange={set('scheme')}>
                {SCHEMES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </FormField>

            <FormField label="Scheme reference">
              <input value={form.scheme_ref} onChange={set('scheme_ref')} placeholder="e.g. DPS-1234567" />
            </FormField>

            <FormField label="Date deposit received *">
              <input type="date" value={form.received_date} onChange={set('received_date')} />
            </FormField>

            <FormField label="Date protected">
              <input type="date" value={form.protected_date} onChange={set('protected_date')} />
            </FormField>

            <FormField label="Prescribed information served">
              <input type="date" value={form.prescribed_info_date} onChange={set('prescribed_info_date')} />
            </FormField>

            <FormField label="Status">
              <select value={form.status} onChange={set('status')}>
                <option value="unprotected">Unprotected</option>
                <option value="protected">Protected</option>
                <option value="returned">Returned to tenant</option>
              </select>
            </FormField>

            <FormField label="Notes">
              <textarea value={form.notes} onChange={set('notes')} rows={3} placeholder="Any additional notes…" />
            </FormField>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button loading={saving} onClick={handleSave} disabled={!selectedProp || !form.received_date || !form.amount_gbp}>
                {editID ? 'Save changes' : 'Add deposit'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
